import React, { useRef, useEffect } from "react";
import { Slider } from "antd";
import { useTranslation } from "react-i18next";
import {
  PriceRangeContainer,
  DropdownContainer,
  DropdownContent,
  SliderContainer,
  RangeLabelsContainer,
  RangeLabel,
  RangeLabelText,
  RangeValueText,
} from "./styles";
import FilterDropDownBtn from "./FilterDropDownBtn";

export interface PriceRange {
  min: number;
  max: number;
}

interface PriceRangeFilterProps {
  label: string;
  selectedRange: number[];
  onSelect: (range: number[]) => void;
  min?: number;
  max?: number;
  step?: number;
  isOpen: boolean;
  onToggle: () => void;
}

const PriceRangeFilter: React.FC<PriceRangeFilterProps> = ({
  label,
  selectedRange,
  onSelect,
  min = 0,
  max = 50000,
  step = 10,
  isOpen,
  onToggle,
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        onToggle();
      }
    }
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onToggle]);

  // Handle slider change
  const handleChange = (value: number | number[]) => {
    if (Array.isArray(value)) {
      onSelect(value);
    }
  };

  // Format value for display
  const formatValue = (value: number) => {
    if (language === "ar") {
      return `${value} ريال سعودي`;
    } else {
      return `${value} SAR`;
    }
  };

  // Check if filter is active (not at default values)
  const isActive = selectedRange[0] !== min || selectedRange[1] !== max;

  return (
    <PriceRangeContainer ref={dropdownRef}>
      <FilterDropDownBtn label={label} onClick={onToggle} active={isActive} />
      {/* Dropdown content: Price range filter */}
      {isOpen && (
        <DropdownContainer>
          <DropdownContent>
            {/* Ant Design Slider */}
            <SliderContainer>
              <Slider
                range
                value={selectedRange}
                onChange={handleChange}
                min={min}
                max={max}
                step={step}
                tooltip={{
                  formatter: (value) => formatValue(value as number),
                }}
                styles={{
                  track: {
                    backgroundColor: "#5B3FFF",
                  },
                  rail: {
                    backgroundColor: "#E3E6F0",
                  },
                  handle: {
                    borderColor: "#5B3FFF",
                    backgroundColor: "#5B3FFF",
                  },
                }}
              />
            </SliderContainer>

            {/* Min/Max labels */}
            <RangeLabelsContainer isRtl={language === "ar"}>
              <RangeLabel>
                <RangeLabelText>{t("COMMON.MIN")}</RangeLabelText>
                <RangeValueText>{formatValue(selectedRange[0])}</RangeValueText>
              </RangeLabel>
              <RangeLabel>
                <RangeLabelText>{t("COMMON.MAX")}</RangeLabelText>
                <RangeValueText>{formatValue(selectedRange[1])}</RangeValueText>
              </RangeLabel>
            </RangeLabelsContainer>
          </DropdownContent>
        </DropdownContainer>
      )}
    </PriceRangeContainer>
  );
};

export default PriceRangeFilter;
